import { ErrorTranslation } from '@surface-elements/shared/util-error';

/**
 * OMS application-specific error translation overrides.
 * These are the highest priority and can override any domain or base translations.
 */
export const OMS_ERROR_TRANSLATIONS: ErrorTranslation[] = [
  // OMS-specific overrides for better UX
  {
    code: 'oms.orders.not_found',
    message:
      "We couldn't find that order. It may have been archived or you may need different permissions. Please contact support if you believe this is an error.",
    title: 'Order Not Found',
    matcher: error =>
      error.status === 404 &&
      error.error?.message === 'orderId not found!' &&
      window.location.hash.includes('/orders'),
    priority: 15, // Highest priority for app-specific override
  },

  // More helpful message for duplicate accounts in OMS context
  {
    code: 'oms.accounts.duplicate',
    message:
      'This account name is already in use. Try adding a location or identifier to make it unique (e.g., "ABC Corp - NYC" or "ABC Corp #2").',
    title: 'Account Name Taken',
    matcher: error =>
      error.error?.message === 'duplicate' &&
      error.url?.includes('/accounts') &&
      window.location.hash.includes('/accounts'),
    priority: 15,
  },

  // More helpful message for duplicate jobs in OMS context
  {
    code: 'oms.jobs.duplicate',
    message:
      'This job name already exists for this account. Try adding a date, phase, or identifier (e.g., "Office Renovation - Phase 2" or "Office Renovation 2024").',
    title: 'Job Name Already Used',
    matcher: error =>
      error.error?.message === 'duplicate' &&
      error.url?.includes('/jobs') &&
      window.location.hash.includes('/jobs'),
    priority: 15,
  },

  // Login page specific error - invalid credentials
  {
    code: 'oms.login.invalid_credentials',
    message:
      'The email or password you entered is incorrect. Please try again or contact your administrator for password assistance.',
    title: 'Invalid Credentials',
    matcher: error =>
      error.status === 401 &&
      error.error?.message === 'Invalid credentials' &&
      window.location.hash.includes('/login'),
    priority: 12,
  },

  // Session timeout with more context
  {
    code: 'oms.session.expired',
    message:
      'Your session has expired for security reasons. Please log in again to continue where you left off.',
    title: 'Session Timeout',
    matcher: error =>
      error.status === 401 && !window.location.hash.includes('/login'),
    priority: 12,
  },

  // Network error with OMS-specific help
  {
    code: 'oms.network.offline',
    message:
      'Cannot connect to Surface Elements servers. Please check your internet connection. If the problem persists, contact IT support.',
    title: 'Connection Lost',
    matcher: error =>
      error.status === 0 && error.url?.includes(window.location.hostname),
    priority: 10,
  },

  // Permission denied (403) - more helpful than base translation
  {
    code: 'oms.permission.denied',
    message:
      "You don't have permission to perform this action. Contact your administrator to request access.",
    title: 'Permission Denied',
    matcher: error => error.status === 403,
    priority: 10,
  },

  // Server errors with actionable guidance
  {
    code: 'oms.server.error',
    message:
      'The server encountered an error. Our team has been notified. Please try again in a few minutes.',
    title: 'Server Error',
    matcher: error => error.status === 500,
    priority: 10,
  },

  {
    code: 'oms.server.unavailable',
    message:
      "The server is temporarily unavailable. We're working to restore service. Please try again shortly.",
    title: 'Service Unavailable',
    matcher: error => error.status === 503,
    priority: 10,
  },

  {
    code: 'oms.server.bad_gateway',
    message:
      'Unable to reach the server. This may be a temporary network issue. Please try again.',
    title: 'Connection Issue',
    matcher: error => error.status === 502,
    priority: 10,
  },

  // Rate limiting
  {
    code: 'oms.rate_limit.exceeded',
    message:
      "You've made too many requests. Please wait a moment before trying again.",
    title: 'Too Many Requests',
    matcher: error => error.status === 429,
    priority: 10,
  },

  // Request timeout
  {
    code: 'oms.request.timeout',
    message:
      'The request took too long to complete. Please check your connection and try again.',
    title: 'Request Timeout',
    matcher: error => error.status === 408,
    priority: 10,
  },

  // Validation errors with helpful context
  {
    code: 'oms.validation.failed',
    message:
      'Please check your input. Some fields contain invalid or missing information.',
    title: 'Validation Error',
    matcher: error =>
      error.status === 400 &&
      (error.error?.message?.toLowerCase().includes('validation') ||
        error.error?.message?.toLowerCase().includes('invalid')),
    priority: 12,
  },

  // PDF report generation errors (actual endpoint: /reports/pdf/{type})
  {
    code: 'oms.reports.pdf.generation_failed',
    message:
      'Unable to generate the PDF report. Please try again or contact support if the issue persists.',
    title: 'PDF Generation Failed',
    matcher: error =>
      error.url?.includes('/reports/pdf') && error.status >= 400,
    priority: 12,
  },

  // Generic bad request with context
  {
    code: 'oms.request.invalid',
    message:
      'The request could not be processed. Please check your input and try again.',
    title: 'Invalid Request',
    matcher: error => error.status === 400,
    priority: 8,
  },
];

/**
 * OMS Application error configuration
 */
export const OMS_ERROR_CONFIG = {
  translations: OMS_ERROR_TRANSLATIONS,
  appName: 'oms',
  enableContextDetection: true,
  defaultMessage:
    'An unexpected error occurred. Please try again or contact support if the problem continues.',
};
